import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import "react-phone-input-2/lib/bootstrap.css";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  useLazyGetUserByIdQuery,
  useUpdateUsersMutation,
} from "../../services/user";
import { showError, showToast } from "../../constants/toasts";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { UploadMedia } from "../../utils/mediaUpload";
import PhoneInput from "react-phone-input-2";
import { ageOptions } from "../../utils/constant";

const EditUser = () => {
  const [profileImage, setProfileImage] = useState<string>("");
  const [imageId, setImageId] = useState<number | null>(null);
  const [userById] = useLazyGetUserByIdQuery();
  const [updateUser] = useUpdateUsersMutation();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const response = await userById({ id: numericId }).unwrap();
      if (
        (response as any).status === 200 ||
        (response as any).statusCode === 200
      ) {
        setUser((response as any).data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (user?.profile_picture?.media_file_url) {
      setProfileImage(user.profile_picture.media_file_url);
    }
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || "",
      country_code: user?.country_code?.replace("+", "") || "",
      gender:
        typeof user?.gender === "string"
          ? user?.gender
          : typeof user?.gender === "number"
          ? user?.gender === 1
            ? "male"
            : user?.gender === 2
            ? "female"
            : "other"
          : "",
      Bot_voice: typeof user?.Bot_voice === "number" ? user?.Bot_voice : "",
      date_of_birth: user?.date_of_birth || "",
      preferred_language: user?.preferred_language || "",
      age_group: user?.age_group || "",
      food_type: user?.food_type || "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .min(2, "At least 2 characters")
        .required("Required"),
      last_name: Yup.string()
        .min(2, "At least 2 characters")
        .required("Required"),
      email: Yup.string().email("Invalid email").required("Required"),
      phone_number: Yup.string()
        .matches(/^[0-9]{7,15}$/i, "Enter valid phone")
        .required("Required"),
      country_code: Yup.string().required("Required"),
      gender: Yup.string()
        .oneOf(["male", "female", "other"], "Select gender")
        .required("Required"),
      Bot_voice: Yup.number()
        .oneOf([1, 2, 3], "Select voice")
        .required("Required"),
      date_of_birth: Yup.string().nullable(),
      preferred_language: Yup.string().nullable(),
      age_group: Yup.string().nullable(),
      food_type: Yup.string().nullable(),
    }),
    onSubmit: async (values) => {
      const numericId = Number(id);
      if (Number.isNaN(numericId)) return;
      const body: any = {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone_number: values.phone_number,
        country_code: `+${values.country_code}`,
        gender: values.gender,
        Bot_voice: values.Bot_voice,
        date_of_birth: values.date_of_birth || null,
        preferred_language: values.preferred_language,
      };
      if (imageId) {
        body.profile_picture = imageId;
      } else if (user?.profile_picture?.id) {
        body.profile_picture = user.profile_picture.id;
      }
      try {
        const response = await updateUser({ id: numericId, body }).unwrap();
        if (response?.status === 200) {
          showToast("User updated successfully");
          navigate("/manage-users");
        }
      } catch (error: any) {
        showError(error?.data?.message || "Failed to update user");
      }
    },
  });

  useEffect(() => {
    if (!user) return;
    formik.setValues({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email || "",
      phone_number: user.phone_number || "",
      country_code: (user.country_code || "").replace("+", ""),
      gender:
        typeof user.gender === "string"
          ? user.gender
          : typeof user.gender === "number"
          ? user.gender === 1
            ? "male"
            : user.gender === 2
            ? "female"
            : "other"
          : "",
      Bot_voice: typeof user.Bot_voice === "number" ? user.Bot_voice : "",
      date_of_birth: user.date_of_birth || "",
      preferred_language: user.preferred_language || "",
      age_group: user.age_group || "",
      food_type: user.food_type || "",
    });
    if (user.profile_picture?.media_file_url) {
      setProfileImage(user.profile_picture.media_file_url);
    }
    if (user.profile_picture?.id) {
      setImageId(user.profile_picture.id);
    }
  }, [user]);

  console.log(user, "user details");

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;
    if (!file) {
      setProfileImage("");
      setImageId(null);
      return;
    }
    const response = (await UploadMedia(file)) as any;
    if (
      response?.data &&
      Array.isArray(response.data) &&
      response.data.length > 0
    ) {
      const uploaded = response.data[0];
      if (uploaded.media_file_url && uploaded.id) {
        setProfileImage(uploaded.media_file_url);
        setImageId(uploaded.id);
      }
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);

  return (
    <>
      <div className="main_title">
        <h1>Edit User</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate("/manage-users")}>User Management</span>{" "}
          - Edit
        </p>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group">
              <label>Upload Image</label>
              {profileImage ? (
                <div className="upload_image ">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={profileImage}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon onClick={() => setProfileImage("")} />
                  </div>
                </div>
              ) : (
                <label className="upload_image " htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={(e: any) => {
                      handleProfileUpload(e);
                      e.target.value = "";
                    }}
                  />
                  <span className="upload_image_holder">
                    <figure className="add_img">
                      <img src="/static/images/placeholder.png" alt="icon" />
                      <span className="upload_img_icon">
                        <img src="/static/images/edit2_icon.svg" alt="icon" />
                      </span>
                    </figure>
                  </span>
                </label>
              )}
            </div>
            <div className="control_group w_33">
              <label>First Name</label>

              <TextField
                name="first_name"
                placeholder="First Name"
                hiddenLabel
                fullWidth
                value={formik.values.first_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.first_name && Boolean(formik.errors.first_name)
                }
                helperText={
                  formik.touched.first_name &&
                  (formik.errors.first_name as string)
                }
              ></TextField>
            </div>
            <div className="control_group w_33">
              <label>Last Name</label>

              <TextField
                name="last_name"
                placeholder="Last Name"
                hiddenLabel
                fullWidth
                value={formik.values.last_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.last_name && Boolean(formik.errors.last_name)
                }
                helperText={
                  formik.touched.last_name &&
                  (formik.errors.last_name as string)
                }
              ></TextField>
            </div>
            <div className="control_group w_33">
              <label>Email</label>

              <TextField
                name="email"
                placeholder="Email"
                hiddenLabel
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={
                  formik.touched.email && (formik.errors.email as string)
                }
              ></TextField>
            </div>
            <div className="control_group w_33">
              <label>Phone Number</label>
              <PhoneInput
                enableSearch
                value={`${formik.values.country_code}${formik.values.phone_number}`}
                country={"in"}
                inputClass="phoneInput"
                onChange={(value: string, country: any) => {
                  const dialCode = country?.dialCode || "";
                  const phoneNumber = value.replace(dialCode, "");
                  formik.setFieldValue("phone_number", phoneNumber);
                  formik.setFieldValue("country_code", dialCode);
                }}
                inputStyle={{ width: "100%" }}
                buttonClass="phoneBtn"
                placeholder="Phone Number"
              />
              {(formik.touched.phone_number && formik.errors.phone_number) ||
              formik.errors.country_code ? (
                <div className="error_text">
                  {(formik.errors as any).phone_number ||
                    (formik.errors as any).country_code}
                </div>
              ) : null}
            </div>
            <div className="control_group w_33">
              <label>Gender</label>

              <Select
                fullWidth
                name="gender"
                value={formik.values.gender || ""}
                onChange={(e) => formik.setFieldValue("gender", e.target.value)}
                onBlur={() => formik.setFieldTouched("gender", true)}
              >
                <MenuItem disabled value="">
                  Select Gender
                </MenuItem>
                <MenuItem value={"male"}>Male</MenuItem>
                <MenuItem value={"female"}>Female</MenuItem>
                <MenuItem value={"other"}>Others</MenuItem>
              </Select>
              {formik.touched.gender && formik.errors.gender ? (
                <div className="error_text">{formik.errors.gender as any}</div>
              ) : null}
            </div>
            <div className="control_group w_33">
              <label>DOB</label>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  className="form_control"
                  slots={{
                    openPickerIcon: () => (
                      <img src="/static/images/date_icon.svg" alt="icon" />
                    ),
                  }}
                  slotProps={{
                    textField: {
                      placeholder: "Select Date",
                      fullWidth: true,
                      hiddenLabel: true,
                    },
                  }}
                  desktopModeMediaQuery="(min-width:0px)"
                  value={
                    formik.values.date_of_birth
                      ? dayjs(formik.values.date_of_birth)
                      : null
                  }
                  onChange={(newValue) =>
                    formik.setFieldValue(
                      "date_of_birth",
                      newValue ? dayjs(newValue).format("YYYY-MM-DD") : ""
                    )
                  }
                />
              </LocalizationProvider>
            </div>
            <div className="control_group w_33">
              <label>Ozri Interaction Voice</label>

              <Select
                fullWidth
                name="Bot_voice"
                value={formik.values.Bot_voice || ""}
                onChange={(e) =>
                  formik.setFieldValue("Bot_voice", Number(e.target.value))
                }
                onBlur={() => formik.setFieldTouched("Bot_voice", true)}
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                <MenuItem value={1}>Calm Female</MenuItem>
                <MenuItem value={2}>Calm Male</MenuItem>
                <MenuItem value={3}>others</MenuItem>
              </Select>
              {formik.touched.Bot_voice && formik.errors.Bot_voice ? (
                <div className="error_text">
                  {formik.errors.Bot_voice as any}
                </div>
              ) : null}
            </div>
            <div className="control_group w_33">
              <label>Language</label>
              <TextField
                name="preferred_language"
                placeholder="Enter Language"
                hiddenLabel
                fullWidth
                value={formik.values.preferred_language}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              ></TextField>
            </div>
            <div className="control_group w_33">
              <label>Food Type</label>

              <Select
                fullWidth
                name="food_type"
                value={formik.values.food_type || "default"}
                onChange={(e) =>
                  formik.setFieldValue("food_type", e.target.value)
                }
                onBlur={() => formik.setFieldTouched("food_type", true)}
              >
                <MenuItem disabled value="default">
                  Select
                </MenuItem>
                <MenuItem value={"veg"}>Veg</MenuItem>
                <MenuItem value={"nonveg"}>Non Veg</MenuItem>
              </Select>
              {formik.touched.food_type && formik.errors.food_type ? (
                <div className="error_text">
                  {formik.errors.food_type as any}
                </div>
              ) : null}
            </div>
            <div className="control_group w_33">
              <label>Age Group</label>

              <Select
                fullWidth
                name="age_group"
                value={formik.values.age_group || ""}
                onChange={(e) =>
                  formik.setFieldValue("age_group", e.target.value)
                }
                onBlur={() => formik.setFieldTouched("age_group", true)}
              >
                <MenuItem disabled value="">
                  Select
                </MenuItem>
                {ageOptions.map((item: any, index: number) => (
                  <MenuItem key={index} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.age_group && formik.errors.age_group ? (
                <div className="error_text">
                  {formik.errors.age_group as any}
                </div>
              ) : null}
            </div>
          </div>
          <div className="form_btn">
            <Button className="btn btn_primary" type="submit">
              Save
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditUser;
